import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../../app/store';
import { selectHypothesisFrame } from '../../../selectors/message';

const selectUnresolvedItems = (state: RootState) => state.unresolvedItems;

// Raw cumulative lists straight off the latest hypothesis frame (source of
// truth — the producer replaces these every event). Absent fields => empty.
const selectAllNotOnMenuCards = createSelector(
  selectHypothesisFrame,
  (frame) => frame?.data?.not_on_menu_cards ?? []
);

const selectAllInvalidOptionLines = createSelector(
  selectHypothesisFrame,
  (frame) => frame?.data?.invalid_option_lines ?? []
);

const selectAllQuantityEscalationItems = createSelector(
  selectHypothesisFrame,
  (frame) => frame?.data?.quantity_escalation_items ?? []
);

const selectAllNoDefaultOptionConstraints = createSelector(
  selectHypothesisFrame,
  (frame) => frame?.data?.no_default_option_constraints ?? []
);

const selectAllUnmatchedCoupons = createSelector(
  selectHypothesisFrame,
  (frame) => frame?.data?.unmatched_coupons ?? []
);

// Cards visible to the agent — empty when the panel has been dismissed.
const selectVisibleNotOnMenuCards = createSelector(
  selectAllNotOnMenuCards,
  selectUnresolvedItems,
  (cards, { allDismissed }) => (allDismissed ? [] : cards)
);

const selectVisibleInvalidOptionLines = createSelector(
  selectAllInvalidOptionLines,
  selectUnresolvedItems,
  (lines, { allDismissed }) => (allDismissed ? [] : lines)
);

const selectVisibleQuantityEscalationItems = createSelector(
  selectAllQuantityEscalationItems,
  selectUnresolvedItems,
  (items, { allDismissed }) => (allDismissed ? [] : items)
);

const selectVisibleNoDefaultOptionConstraints = createSelector(
  selectAllNoDefaultOptionConstraints,
  selectUnresolvedItems,
  (entries, { allDismissed }) => (allDismissed ? [] : entries)
);

const selectVisibleUnmatchedCoupons = createSelector(
  selectAllUnmatchedCoupons,
  selectUnresolvedItems,
  (coupons, { allDismissed }) => (allDismissed ? [] : coupons)
);

const selectUnresolvedItemsCount = createSelector(
  selectVisibleNotOnMenuCards,
  selectVisibleInvalidOptionLines,
  selectVisibleQuantityEscalationItems,
  selectVisibleNoDefaultOptionConstraints,
  selectVisibleUnmatchedCoupons,
  (cards, lines, qtyItems, noDefaults, coupons) =>
    cards.length +
    lines.length +
    qtyItems.length +
    noDefaults.length +
    coupons.length
);

export {
  selectAllNotOnMenuCards,
  selectAllInvalidOptionLines,
  selectAllQuantityEscalationItems,
  selectAllNoDefaultOptionConstraints,
  selectAllUnmatchedCoupons,
  selectVisibleNotOnMenuCards,
  selectVisibleInvalidOptionLines,
  selectVisibleQuantityEscalationItems,
  selectVisibleNoDefaultOptionConstraints,
  selectVisibleUnmatchedCoupons,
  selectUnresolvedItemsCount,
};
